import { ArrowRightIcon } from "@heroicons/react/outline";

import { useEvent } from "../../changelog-item-context";
import EntityLink from "../EntityLink";
import { getVersionEntityLink } from "../utils";

import EventTitleWrapper, { ActionText } from "./EventTitleWrapper";

import LabelBadge from "@/components/ui/LabelBadge";
import { EntityType } from "@/queries/folder-entities/types";

type LabelAction = "created" | "deleted" | "moved";

interface BaseLabelEventContentProps {
  entityType: EntityType;
  entityName: string;
  workspaceId: number;
  entityId: string | null;
  labelName?: string;
}

interface SingleVersionLabelEventContentProps extends BaseLabelEventContentProps {
  action: "created" | "deleted";
  version: number | undefined;
}

interface MoveLabelEventContentProps extends BaseLabelEventContentProps {
  action: "moved";
  fromVersion: number | undefined;
  toVersion: number | undefined;
}

type LabelEventContentProps = SingleVersionLabelEventContentProps | MoveLabelEventContentProps;

const ACTION_LABELS: Record<
  LabelAction,
  { verb: string; requestVerb: string; preposition: string }
> = {
  created: { verb: "Created label", requestVerb: "Requested label creation", preposition: "at" },
  deleted: { verb: "Deleted label", requestVerb: "Requested label deletion", preposition: "at" },
  moved: { verb: "Moved label", requestVerb: "Requested to move label", preposition: "from" },
};

const LabelEventContent = (props: LabelEventContentProps) => {
  const { action, entityType, entityName, workspaceId, entityId, labelName } = props;
  const { approved, merged, approved_by } = useEvent();
  const { verb, requestVerb, preposition } = ACTION_LABELS[action];

  // Direct action: merged=true, approved=true, approved_by=null (bypassed approval workflow)
  // Also direct for "created" action which doesn't require approval
  const isDirectAction = (merged && approved && approved_by === null) || action === "created";
  const displayVerb = isDirectAction ? verb : requestVerb;

  if (action === "moved") {
    const { fromVersion, toVersion } = props;

    if (!fromVersion || !toVersion || !entityId) {
      return null;
    }

    const fromLink = getVersionEntityLink(workspaceId, entityType, entityId, fromVersion);
    const toLink = getVersionEntityLink(workspaceId, entityType, entityId, toVersion);

    return (
      <EventTitleWrapper>
        <ActionText>{displayVerb}</ActionText>
        {labelName && <LabelBadge label={labelName} variant="environment" />}
        <ActionText>{preposition}</ActionText>
        <EntityLink
          type={entityType}
          name={entityName}
          version={fromVersion}
          link={fromLink}
          className="opacity-60 hover:opacity-100"
        />
        <ArrowRightIcon className="h-3 w-3 flex-shrink-0 text-gray-400" />
        <EntityLink type={entityType} name={entityName} version={toVersion} link={toLink} />
      </EventTitleWrapper>
    );
  }

  const { version } = props;

  if (!version || !entityId) {
    return null;
  }

  const link = getVersionEntityLink(workspaceId, entityType, entityId, version);

  return (
    <EventTitleWrapper>
      <ActionText>{displayVerb}</ActionText>
      {labelName && <LabelBadge label={labelName} variant="environment" />}
      <ActionText>{preposition}</ActionText>
      <EntityLink type={entityType} name={entityName} version={version} link={link} />
    </EventTitleWrapper>
  );
};

export default LabelEventContent;
